import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Select from 'react-select';

import {
    useGetCallCredentialsQuery,
    useUpdateCallCredentialsMutation,
    useGetCallTimerQuery,
    useUpdateCallTimerMutation,

    // Add these hooks in Api.js
    useGetAsteriskStatusQuery,
    useTestTrunkConnectionMutation,
    useReloadDialplanMutation,
    useReloadPjsipMutation,
    useRestartAsteriskMutation
} from './Api';

import Loader from '../loader/loader';
import toastService from '../../services/toastService';

const CallSettings = () => {

    /* ==========================================================
       FORM VALIDATION
    ========================================================== */

    const validationSchema = Yup.object().shape({
        providerName: Yup.string()
            .required('Provider name is required')
            .trim()
            .max(100, 'Provider name is too long'),

        trunkName: Yup.string()
            .required('Trunk name is required')
            .matches(
                /^[a-zA-Z0-9_-]+$/,
                'Use only letters, numbers, underscore and hyphen'
            )
            .trim(),

        sipDomain: Yup.string()
            .required('SIP domain is required')
            .trim(),

        sipPort: Yup.number()
            .typeError('SIP port is required')
            .required('SIP port is required')
            .min(1, 'Invalid SIP port')
            .max(65535, 'Invalid SIP port'),

        username: Yup.string()
            .required('Username is required')
            .trim(),

        password: Yup.string()
            .nullable(),

        fromUser: Yup.string()
            .nullable()
            .trim(),

        didNumber: Yup.string()
            .required('DID number is required')
            .matches(
                /^\+?[0-9]+$/,
                'DID number must contain only digits and optional +'
            )
            .trim(),

        codecs: Yup.string()
            .required('At least one codec is required')
            .trim(),

        inboundContext: Yup.string()
            .required('Inbound context is required')
            .matches(
                /^[a-zA-Z0-9_-]+$/,
                'Invalid inbound context'
            )
            .trim(),

        dialPrefix: Yup.string()
            .nullable()
            .matches(
                /^[0-9]*$/,
                'Dial prefix must contain only numbers'
            )
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: {
            errors
        }
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'all',
        defaultValues: {
            providerName: '',
            trunkName: '',
            sipDomain: '',
            sipPort: 5060,
            username: '',
            password: '',
            fromUser: '',
            didNumber: '',
            codecs: 'ulaw,alaw',
            inboundContext: 'from-twilio',
            dialPrefix: '',
            is_active: false
        }
    });

    /* ==========================================================
       TRUNK API
    ========================================================== */

    const {
        data: trunkData,
        isLoading: trunkLoading,
        refetch: refetchTrunk
    } = useGetCallCredentialsQuery();

    const [
        updateCredentialsApi,
        {
            isLoading: updateCredentialsLoading
        }
    ] = useUpdateCallCredentialsMutation();

    /* ==========================================================
       ASTERISK STATUS API
    ========================================================== */

    const {
        data: asteriskStatusData,
        isLoading: asteriskStatusLoading,
        refetch: refetchAsteriskStatus
    } = useGetAsteriskStatusQuery();

    const [
        testTrunkConnectionApi,
        {
            isLoading: testConnectionLoading
        }
    ] = useTestTrunkConnectionMutation();

    const [
        reloadDialplanApi,
        {
            isLoading: reloadDialplanLoading
        }
    ] = useReloadDialplanMutation();

    const [
        reloadPjsipApi,
        {
            isLoading: reloadPjsipLoading
        }
    ] = useReloadPjsipMutation();

    const [
        restartAsteriskApi,
        {
            isLoading: restartAsteriskLoading
        }
    ] = useRestartAsteriskMutation();

    /* ==========================================================
       TIMER API
    ========================================================== */

    const {
        data: getTimerData,
        isLoading: getTimerLoading
    } = useGetCallTimerQuery();

    const [
        updateTimerApi,
        {
            isLoading: updateTimerLoading
        }
    ] = useUpdateCallTimerMutation();

    /* ==========================================================
       TRUNK STATE
    ========================================================== */

    const callDirectionOptions = [
        {
            value: 'incoming',
            label: 'Incoming Only'
        },
        // {
        //     value: 'outgoing',
        //     label: 'Outgoing Only'
        // },
        // {
        //     value: 'both',
        //     label: 'Incoming & Outgoing'
        // }
    ];

    const [dtmfMode, setDtmfMode] = useState('rfc4733');
    const [callDirection, setCallDirection] = useState('incoming');

    const [
        registrationEnabled,
        setRegistrationEnabled
    ] = useState(true);

    const [
        trunkEnabled,
        setTrunkEnabled
    ] = useState(true);

    /* ==========================================================
       STATUS STATE
    ========================================================== */

    const [connectionStatus, setConnectionStatus] = useState({
        asteriskStatus: 'unknown',
        registrationStatus: 'unknown',
        activeChannels: 0,
        activeCalls: 0,
        lastReload: null,
        lastCheckedAt: null,
        statusMessage: ''
    });

    /* ==========================================================
       LOAD TRUNK DATA
    ========================================================== */

    useEffect(() => {
        if (!trunkData?.data) return;
        const config = trunkData.data;
        console.log('config ', config)
        const endpoint = config.psEndpoint || {};
        const auth = config.psAuth || {};
        const aor = config.psAor || {};
        const identify = config.psIdentify || {};
        const registration = config.psRegistration || {};
        reset({
            providerName:
                config.provider_name ||
                config.call_provider_name ||
                '',

            trunkName:
                config.trunk_name ||
                config.endpoint_id ||
                '',

            sipDomain:
                aor.contact
                    ? aor.contact
                        .replace("sip:", "")
                        .split(":")[0]
                    : "",

            sipPort:
                Number(
                    aor.contact?.split(":")[2] || 5060
                ),

            username:
                auth.username || "",

            // Never load the real password into Angular/React
            password: '',

            fromUser:
                endpoint.from_user || "",

            didNumber:
                config.did_number || "",

            codecs:
                endpoint.allow ||
                'ulaw,alaw',

            inboundContext:
                endpoint.context ??
                'from-twilio',

            dialPrefix:
                config.dial_prefix ||
                ''
        });

        setCallDirection(
            config.call_direction ||
            'incoming'
        );


        setRegistrationEnabled(
            config.registration_enabled === undefined
                ? true
                : Boolean(config.registration_enabled)
        );

        setTrunkEnabled(
            config.is_active === undefined
                ? true
                : Boolean(config.is_active)
        );

    }, [trunkData, reset]);

    /* ==========================================================
       LOAD ASTERISK STATUS
    ========================================================== */

    useEffect(() => {
        if (!asteriskStatusData?.data) return;
        const status = asteriskStatusData.data;

        setConnectionStatus({
            asteriskStatus:
                status.asterisk_status ||
                status.asteriskStatus ||
                'unknown',

            registrationStatus:
                status.registration_status ||
                status.registrationStatus ||
                'unknown',

            activeChannels:
                Number(
                    status.active_channels ||
                    status.activeChannels ||
                    0
                ),

            activeCalls:
                Number(
                    status.active_calls ||
                    status.activeCalls ||
                    0
                ),

            lastReload:
                status.last_reload ||
                status.lastReload ||
                null,

            lastCheckedAt:
                status.last_checked_at ||
                status.lastCheckedAt ||
                null,

            statusMessage:
                status.message ||
                ''
        });

    }, [asteriskStatusData]);

    /* ==========================================================
       SAVE TRUNK CONFIGURATION
    ========================================================== */

    const handleOnSubmit = async (formData) => {
        try {
            const payload = {
                provider_name:
                    formData.providerName.trim(),

                trunk_name:
                    formData.trunkName.trim(),

                sip_domain:
                    formData.sipDomain.trim(),

                sip_port:
                    Number(formData.sipPort),

                username:
                    formData.username.trim(),

                from_user:
                    formData.fromUser?.trim() || null,

                did_number:
                    formData.didNumber.trim(),

                codecs:
                    formData.codecs.trim(),

                inbound_context:
                    formData.inboundContext.trim(),

                dial_prefix:
                    formData.dialPrefix?.trim() || null,

                call_direction:
                    callDirection,

                registration_enabled:
                    registrationEnabled,

                is_active:
                    trunkEnabled
            };

            /*
             * Only send the password when the admin enters
             * a new password. Otherwise, backend must keep
             * the current password.
             */
            if (formData.password?.trim()) {
                payload.password =
                    formData.password.trim();
            }

            const response =
                await updateCredentialsApi(payload).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'Unable to update trunk configuration'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'Trunk configuration updated successfully'
            );

            await Promise.all([
                refetchTrunk(),
                refetchAsteriskStatus()
            ]);

        } catch (error) {
            console.error(
                'Update trunk configuration error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                error?.message ||
                'Unable to update trunk configuration'
            );
        }
    };

    /* ==========================================================
       TEST CONNECTION
    ========================================================== */

    const handleTestConnection = async () => {
        try {
            const response =
                await testTrunkConnectionApi({}).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'Connection test failed'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'Connection test completed successfully'
            );

            if (response?.data) {
                setConnectionStatus((previous) => ({
                    ...previous,

                    asteriskStatus:
                        response.data.asterisk_status ||
                        response.data.asteriskStatus ||
                        previous.asteriskStatus,

                    registrationStatus:
                        response.data.registration_status ||
                        response.data.registrationStatus ||
                        previous.registrationStatus,

                    activeChannels:
                        Number(
                            response.data.active_channels ??
                            response.data.activeChannels ??
                            previous.activeChannels
                        ),

                    activeCalls:
                        Number(
                            response.data.active_calls ??
                            response.data.activeCalls ??
                            previous.activeCalls
                        ),

                    lastCheckedAt:
                        response.data.checked_at ||
                        response.data.checkedAt ||
                        new Date().toISOString(),

                    statusMessage:
                        response.data.message ||
                        response.message ||
                        ''
                }));
            }

            await refetchAsteriskStatus();

        } catch (error) {
            console.error(
                'Test connection error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                'Unable to connect to Asterisk or SIP trunk'
            );
        }
    };

    /* ==========================================================
       RELOAD DIALPLAN
    ========================================================== */

    const handleReloadDialplan = async () => {
        try {
            const response =
                await reloadDialplanApi({}).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'Dialplan reload failed'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'Dialplan reloaded successfully'
            );

            setConnectionStatus((previous) => ({
                ...previous,
                lastReload:
                    response?.data?.last_reload ||
                    response?.data?.lastReload ||
                    new Date().toISOString()
            }));

            await refetchAsteriskStatus();

        } catch (error) {
            console.error(
                'Reload dialplan error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                'Unable to reload dialplan'
            );
        }
    };

    /* ==========================================================
       RELOAD PJSIP
    ========================================================== */

    const handleReloadPjsip = async () => {
        try {
            const response =
                await reloadPjsipApi({}).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'PJSIP reload failed'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'PJSIP reloaded successfully'
            );

            setConnectionStatus((previous) => ({
                ...previous,
                lastReload:
                    response?.data?.last_reload ||
                    response?.data?.lastReload ||
                    new Date().toISOString()
            }));

            await refetchAsteriskStatus();

        } catch (error) {
            console.error(
                'Reload PJSIP error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                'Unable to reload PJSIP'
            );
        }
    };

    /* ==========================================================
       RESTART ASTERISK
    ========================================================== */

    const handleRestartAsterisk = async () => {
        const confirmed = window.confirm(
            'Restarting Asterisk may disconnect active calls. Do you want to continue?'
        );

        if (!confirmed) return;

        try {
            const response =
                await restartAsteriskApi({}).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'Asterisk restart failed'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'Asterisk restart initiated successfully'
            );

            setConnectionStatus((previous) => ({
                ...previous,
                asteriskStatus: 'restarting'
            }));

            /*
             * Give Asterisk a few seconds before checking status.
             */
            window.setTimeout(async () => {
                await refetchAsteriskStatus();
            }, 5000);

        } catch (error) {
            console.error(
                'Restart Asterisk error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                'Unable to restart Asterisk'
            );
        }
    };

    /* ==========================================================
       TIMER STATE
    ========================================================== */

    const timerOptions = [
        {
            value: 'MM:SS',
            label: 'MM:SS'
        },
        {
            value: 'HH:MM:SS',
            label: 'HH:MM:SS'
        }
    ];

    const [timerFormat, setTimerFormat] =
        useState('MM:SS');

    const [duration, setDuration] = useState({
        greenMax: 5,
        yellowMax: 10
    });

    useEffect(() => {
        if (!getTimerData?.data) return;

        setDuration({
            greenMax:
                Number(
                    getTimerData.data
                        ?.duration_thresholds
                        ?.green || 5
                ),

            yellowMax:
                Number(
                    getTimerData.data
                        ?.duration_thresholds
                        ?.yellow || 10
                )
        });

        setTimerFormat(
            getTimerData.data.timer_format ||
            'MM:SS'
        );

    }, [getTimerData]);

    const handleGreenChange = (event) => {
        const value = Number(event.target.value);

        setDuration((previous) => ({
            ...previous,
            greenMax: value
        }));
    };

    const handleYellowChange = (event) => {
        const value = Number(event.target.value);

        setDuration((previous) => ({
            ...previous,
            yellowMax: value
        }));
    };

    const handleOnSubmitTimer = async () => {
        try {
            if (duration.greenMax <= 0) {
                toastService.error(
                    'Green threshold must be greater than 0.'
                );
                return;
            }

            if (
                duration.yellowMax <=
                duration.greenMax
            ) {
                toastService.error(
                    'Yellow threshold must be greater than Green threshold.'
                );
                return;
            }

            const payload = {
                timer_format:
                    timerFormat,

                duration_thresholds: {
                    green:
                        duration.greenMax,

                    yellow:
                        duration.yellowMax
                }
            };

            const response =
                await updateTimerApi(payload).unwrap();

            if (!response?.success) {
                toastService.error(
                    response?.message ||
                    'Unable to update timer configuration'
                );
                return;
            }

            toastService.success(
                response?.message ||
                'Call timer configuration updated successfully'
            );

        } catch (error) {
            console.error(
                'Update timer error:',
                error
            );

            toastService.error(
                error?.data?.message ||
                error?.message ||
                'Unable to update timer configuration'
            );
        }
    };

    /* ==========================================================
       UI HELPERS
    ========================================================== */

    const normalizeStatus = (value) => {
        return String(value || '')
            .trim()
            .toLowerCase();
    };

    const isAsteriskOnline = [
        'online',
        'connected',
        'active',
        'running'
    ].includes(
        normalizeStatus(
            connectionStatus.asteriskStatus
        )
    );

    const isRegistered = [
        'registered',
        'online',
        'connected',
        'active'
    ].includes(
        normalizeStatus(
            connectionStatus.registrationStatus
        )
    );

    const formatDateTime = (value) => {
        if (!value) return '--';

        const parsedDate = new Date(value);

        if (Number.isNaN(parsedDate.getTime())) {
            return value;
        }

        return parsedDate.toLocaleString();
    };

    const pageLoading =
        trunkLoading ||
        updateCredentialsLoading ||
        getTimerLoading ||
        updateTimerLoading ||
        asteriskStatusLoading ||
        testConnectionLoading ||
        reloadDialplanLoading ||
        reloadPjsipLoading ||
        restartAsteriskLoading;

    /* ==========================================================
       RENDER
    ========================================================== */

    if (pageLoading) {
        return <Loader />;
    }

    return (
        <div className="call-settings-page">

            {/* PAGE HEADER */}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">

                <div>
                    <h3 className="fw-bold mb-1">
                        Asterisk Trunk Configuration
                    </h3>

                    <p className="text-muted mb-0">
                        Manage realtime SIP trunk configuration,
                        connection status and Asterisk actions
                    </p>
                </div>

                <button
                    type="submit"
                    form="trunk-configuration-form"
                    className="btn btn-primary px-4"
                    disabled={updateCredentialsLoading}
                >
                    {updateCredentialsLoading
                        ? 'Saving...'
                        : 'Save Configuration'}
                </button>

            </div>

            {/* STATUS CARDS */}

            <div className="row g-3 mb-4">

                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="smtp-card h-100 p-3">
                        <small className="text-muted">
                            Asterisk Status
                        </small>

                        <h4
                            className={
                                isAsteriskOnline
                                    ? 'text-success mt-2'
                                    : 'text-danger mt-2'
                            }
                        >
                            {isAsteriskOnline
                                ? 'Online'
                                : connectionStatus.asteriskStatus ||
                                'Offline'}
                        </h4>

                        <small className="text-muted">
                            {connectionStatus.statusMessage ||
                                'Asterisk server connection'}
                        </small>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="smtp-card h-100 p-3">
                        <small className="text-muted">
                            SIP Registration
                        </small>

                        <h4
                            className={
                                connectionStatus.callDirection === "incoming"
                                    ? "text-secondary mt-2"
                                    : isRegistered
                                        ? "text-success mt-2"
                                        : "text-danger mt-2"
                            }
                        >
                            {
                                connectionStatus.callDirection === "incoming"
                                    ? "Not Required"
                                    : isRegistered
                                        ? "Registered"
                                        : connectionStatus.registrationStatus || "Unregistered"
                            }
                        </h4>

                        <small className="text-muted">
                            {
                                connectionStatus.callDirection === "incoming"
                                    ? "Incoming SIP trunks do not require registration."
                                    : "Trunk registration status"
                            }
                        </small>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="smtp-card h-100 p-3">
                        <small className="text-muted">
                            Active Channels
                        </small>

                        <h4 className="mt-2">
                            {connectionStatus.activeChannels}
                        </h4>

                        <small className="text-muted">
                            {connectionStatus.activeCalls} active calls
                        </small>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="smtp-card h-100 p-3">
                        <small className="text-muted">
                            Last Reload
                        </small>

                        <h6 className="mt-2 mb-1">
                            {formatDateTime(
                                connectionStatus.lastReload
                            )}
                        </h6>

                        <small className="text-muted">
                            Last checked:{' '}
                            {formatDateTime(
                                connectionStatus.lastCheckedAt
                            )}
                        </small>
                    </div>
                </div>

            </div>

            {/* TRUNK FORM */}

            <div className="smtp-card mb-5">

                <form
                    id="trunk-configuration-form"
                    onSubmit={handleSubmit(handleOnSubmit)}
                >
                    <div className="mb-4">
                        <h4 className="fw-bold mb-1">
                            SIP Trunk Configuration
                        </h4>

                        <p className="text-muted mb-0">
                            Update the realtime PJSIP configuration
                            stored in MySQL
                        </p>
                    </div>

                    <div className="row g-3">

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Provider Name</label>

                            <input
                                type="text"
                                {...register('providerName')}
                                className={`form-control ${errors.providerName
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="Tata Tele Business"
                            />

                            <div className="invalid-feedback">
                                {errors.providerName?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Trunk Name / Identifier</label>

                            <input
                                type="text"
                                {...register('trunkName')}
                                className={`form-control ${errors.trunkName
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="tata_india"
                            />

                            <div className="invalid-feedback">
                                {errors.trunkName?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>SIP Domain / Registrar</label>

                            <input
                                type="text"
                                {...register('sipDomain')}
                                className={`form-control ${errors.sipDomain
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="sip.provider.com"
                            />

                            <div className="invalid-feedback">
                                {errors.sipDomain?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>SIP Port</label>

                            <input
                                type="number"
                                {...register('sipPort')}
                                className={`form-control ${errors.sipPort
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                            />

                            <div className="invalid-feedback">
                                {errors.sipPort?.message}
                            </div>
                        </div>



                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Username</label>

                            <input
                                type="text"
                                {...register('username')}
                                className={`form-control ${errors.username
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                            />

                            <div className="invalid-feedback">
                                {errors.username?.message}
                            </div>
                        </div>


                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Password</label>

                            <input
                                type="password"
                                {...register('password')}
                                className="form-control"
                                placeholder="Leave blank to keep existing password"
                                autoComplete="new-password"
                            />
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>From User</label>

                            <input
                                type="text"
                                {...register('fromUser')}
                                className="form-control"
                            />
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>DID Number</label>

                            <input
                                type="text"
                                {...register('didNumber')}
                                className={`form-control ${errors.didNumber
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="+919876543210"
                            />

                            <div className="invalid-feedback">
                                {errors.didNumber?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Codecs</label>

                            <input
                                type="text"
                                {...register('codecs')}
                                className={`form-control ${errors.codecs
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="ulaw,alaw"
                            />

                            <div className="invalid-feedback">
                                {errors.codecs?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Inbound Context</label>

                            <input
                                type="text"
                                {...register('inboundContext')}
                                className={`form-control ${errors.inboundContext
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                            />

                            <div className="invalid-feedback">
                                {errors.inboundContext?.message}
                            </div>
                        </div>


                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Dial Prefix</label>

                            <input
                                type="text"
                                {...register('dialPrefix')}
                                className={`form-control ${errors.dialPrefix
                                    ? 'is-invalid'
                                    : ''
                                    }`}
                                placeholder="91"
                            />

                            <div className="invalid-feedback">
                                {errors.dialPrefix?.message}
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label>Call Direction</label>

                            <Select
                                options={callDirectionOptions}
                                value={callDirectionOptions.find(
                                    (item) =>
                                        item.value === callDirection
                                )}
                                onChange={(option) =>
                                    setCallDirection(
                                        option?.value ||
                                        'incoming'
                                    )
                                }
                                isSearchable={false}
                            />
                        </div>


                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label className="form-label d-block mb-2">
                                Registration
                            </label>

                            <div className="d-flex align-items-center">
                                <label className="switch switch-sm">
                                    <input
                                        type="checkbox"
                                        checked={registrationEnabled}
                                        onChange={(event) =>
                                            setRegistrationEnabled(
                                                event.target.checked
                                            )
                                        }
                                    />
                                    <span className="slider"></span>
                                </label>

                                <span
                                    className={`ms-2 small fw-semibold ${registrationEnabled ? "text-success" : "text-danger"
                                        }`}
                                >
                                    {registrationEnabled ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-xl-4 smtp-field">
                            <label className="form-label d-block mb-2">
                                Trunk Status
                            </label>

                            <div className="d-flex align-items-center">
                                <label className="switch switch-sm">
                                    <input
                                        type="checkbox"
                                        checked={trunkEnabled}
                                        onChange={(e) => setTrunkEnabled(e.target.checked)}
                                    />
                                    <span className="slider"></span>
                                </label>

                                <span
                                    className={`ms-2 small fw-semibold ${trunkEnabled ? "text-success" : "text-danger"
                                        }`}
                                >
                                    {trunkEnabled ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>

                    </div>

                </form>

                {/* SERVER ACTIONS */}

                <div className="border-top mt-4 pt-4">

                    <h5 className="fw-bold mb-3">
                        Server Actions
                    </h5>

                    <div className="row g-3">

                        <div className="col-12 col-sm-6 col-xl-3">
                            <button
                                type="button"
                                className="btn btn-outline-success w-100 py-3"
                                onClick={handleTestConnection}
                                disabled={testConnectionLoading}
                            >
                                Test Connection
                            </button>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100 py-3"
                                onClick={handleReloadDialplan}
                                disabled={reloadDialplanLoading}
                            >
                                Reload Dialplan
                            </button>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100 py-3"
                                onClick={handleReloadPjsip}
                                disabled={reloadPjsipLoading}
                            >
                                Reload PJSIP
                            </button>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-3">
                            <button
                                type="button"
                                className="btn btn-outline-danger w-100 py-3"
                                onClick={handleRestartAsterisk}
                                disabled={restartAsteriskLoading}
                            >
                                Restart Asterisk
                            </button>
                        </div>

                    </div>

                </div>

            </div>

            {/* CALL TIMER */}

  <div className="call-timer-card">

    <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
            <h5 className="fw-bold mb-1">
                Call Timer & Status
            </h5>

            <small className="text-muted">
                Configure call timer and duration status
            </small>
        </div>

        <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={handleOnSubmitTimer}
            disabled={updateTimerLoading}
        >
            {updateTimerLoading ? "Saving..." : "Save"}
        </button>
    </div>

    <div className="row align-items-center g-2">

        <div className="col-md-2">
            <label className="form-label mb-1">
                Timer Format
            </label>

            <Select
                classNamePrefix="timer-select"
                options={timerOptions}
                value={timerOptions.find(
                    item => item.value === timerFormat
                )}
                onChange={(option) =>
                    setTimerFormat(option?.value || "MM:SS")
                }
                isSearchable={false}
            />
        </div>

        <div className="col-md-3">
            <label className="form-label mb-1">
                Green
            </label>

            <div className="input-group input-group-sm">
                <span className="input-group-text bg-success text-white">
                    0 -
                </span>

                <input
                    type="number"
                    className="form-control"
                    value={duration.greenMax}
                    onChange={handleGreenChange}
                />

                <span className="input-group-text">
                    Min
                </span>
            </div>
        </div>

        <div className="col-md-3">
            <label className="form-label mb-1">
                Yellow
            </label>

            <div className="input-group input-group-sm">
                <span className="input-group-text bg-warning">
                    {duration.greenMax} -
                </span>

                <input
                    type="number"
                    className="form-control"
                    value={duration.yellowMax}
                    onChange={handleYellowChange}
                />

                <span className="input-group-text">
                    Min
                </span>
            </div>
        </div>

        <div className="col-md-3">
            <label className="form-label mb-1">
                Red
            </label>

            <div className="input-group input-group-sm">
                <span className="input-group-text bg-danger text-white">
                    &gt;
                </span>

                <input
                    type="number"
                    className="form-control"
                    value={duration.yellowMax}
                    readOnly
                />

                <span className="input-group-text">
                    Min
                </span>
            </div>
        </div>

    </div>

</div>

        </div>
    );
};

export default CallSettings;